import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { usePageTracking } from './hooks/usePageTracking';
import { useAnalyticsContext } from './contexts/AnalyticsContext';
import { usePerformanceTracking } from './hooks/usePerformanceTracking';
import { useCartStore } from './stores/cartStore';
import { useLanguage } from './hooks/useLanguage';

// New routing system
import { AppRouter } from './routing/AppRouter';

// Essential global components
import { CookieConsentPopup } from './components/CookieConsentPopup';
import { SideCart } from './components/SideCart';

// Types
import type { Product, WeightVariant } from './components/ProductCard';
import { mapMenuCategoryToCollection } from './hooks/useShopifyProducts';

// Cookie preferences interface
interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getLocalizedUrl } = useLanguage();

  // Analytics tracking
  usePageTracking();
  usePerformanceTracking();
  const analytics = useAnalyticsContext();

  // Auto-scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Market store is automatically initialized by persist middleware

  // 🎯 СЕЛЕКТИВНЫЕ ПОДПИСКИ НА CartStore - только то что нужно App!
  // ❌ УБРАЛИ cartItems - не подписываемся на них! Они нужны только SideCart
  // ❌ УБРАЛИ isCartOpen - не подписываемся! SideCart сам управляет своим состоянием
  const setCartOpen = useCartStore(state => state.setCartOpen);
  const updateCartItemQuantity = useCartStore(
    state => state.updateCartItemQuantity
  );
  const removeFromCart = useCartStore(state => state.removeFromCart);
  const getTotalItems = useCartStore(state => state.getTotalItems);
  const addToCart = useCartStore(state => state.addToCart); // Для handleAddToCart

  // Other local state
  const [catalogFilter, setCatalogFilter] = useState<{
    category?: string;
    subcategory?: string;
    categories?: string[];
  }>({});
  // Cookie preferences state (kept local as it's not needed to persist)
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(
    {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: 0,
    }
  );

  // Cart functions - now using Zustand store with analytics
  const handleAddToCart = (product: Product, variant?: WeightVariant) => {
    // Analytics tracking
    const analyticsItem = {
      item_id: product.id,
      item_name: product.name,
      category: 'Spices', // Fixed: removed non-existent category property
      item_brand: 'Olispices',
      item_variant: variant?.weight,
      price: variant?.price || product.price || 0,
      quantity: 1,
    };

    analytics.trackAddToCart('USD', analyticsItem.price, [analyticsItem]);

    // Add to cart via Zustand store
    addToCart(product, variant);

    // Toast notification - упрощаем, не нужно проверять существование
    // CartStore сам знает что делать: обновлять количество или добавлять новый
    toast.success(`${product.name} added to cart!`);
  };

  // Cart functions are now handled by Zustand store

  // Navigation handlers
  const handleHomeNavigation = () => navigate(getLocalizedUrl('/'));

  const handleCatalogClick = () => {
    setCatalogFilter({});
    navigate(getLocalizedUrl('/catalog'));
  };

  const handleRecipesClick = () => navigate(getLocalizedUrl('/recipes'));

  const handleMenuClick = () => navigate(getLocalizedUrl('/menu'));

  const handleAccountSettingsClick = () => navigate(getLocalizedUrl('/account-settings'));

  const handleCategoryClick = (category: string, subcategory?: string) => {
    const collection = mapMenuCategoryToCollection(category);
    const filterConfig: any = { category: collection };

    if (subcategory) {
      filterConfig.subcategory = subcategory;
    }

    setCatalogFilter(filterConfig);
    navigate(getLocalizedUrl('/catalog'));
  };

  const handleProductClick = (product: Product) => {
    const analyticsItem = {
      item_id: product.id,
      item_name: product.name,
      category: 'Spices', // Fixed: removed non-existent category property
      item_brand: 'Olispices',
      price: product.price || 0,
      quantity: 1,
    };

    analytics.trackViewItem('USD', analyticsItem.price, [analyticsItem]);
    navigate(getLocalizedUrl(`/product/${product.handle}`));
  };

  const handleRecipeClick = (recipe?: any) => {
    const recipeHandle = recipe?.handle || recipe?.slug;
    if (recipeHandle) {
      analytics.trackRecipeView(
        recipe.id || recipeHandle,
        recipe.title || recipe.name,
        recipe.category || 'Recipe'
      );
      navigate(getLocalizedUrl(`/recipe/${recipeHandle}`));
    } else {
      navigate(getLocalizedUrl('/recipes'));
    }
  };

  const handlePageNavigation = (page: string) => {
    navigate(getLocalizedUrl(`/${page}`));
  };

  const handleCartClick = () => {
    setCartOpen(true);
  };

  const handleCookiePreferencesChange = (preferences: CookiePreferences) => {
    setCookiePreferences(preferences);
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        {/* Toast notifications - Адаптивная позиция */}
        <Toaster
          position={window.innerWidth >= 640 ? 'bottom-left' : 'bottom-center'}
          toastOptions={{
            duration: 3000,
            style: {
              background: '#f59e0b',
              color: 'white',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: '0 8px 32px rgba(245, 158, 11, 0.3)',
            },
          }}
        />

        {/* Main Router */}
        <AppRouter
          onCartClick={handleCartClick}
          onCatalogClick={handleCatalogClick}
          onRecipesClick={handleRecipesClick}
          onMenuClick={handleMenuClick}
          onCategoryClick={handleCategoryClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onPageNavigation={handlePageNavigation}
          onHomeNavigation={handleHomeNavigation}
          onProductClick={handleProductClick}
          onRecipeClick={handleRecipeClick}
          onAddToCart={handleAddToCart}
          catalogFilter={catalogFilter}
        />

        {/* Global Components */}
        <CookieConsentPopup
          onPreferencesChange={handleCookiePreferencesChange}
          onOpenAccountSettings={handleAccountSettingsClick}
        />

        <SideCart
          onClose={() => setCartOpen(false)}
          onUpdateQuantity={updateCartItemQuantity}
          onRemoveItem={removeFromCart}
          onCartPageClick={() => navigate(getLocalizedUrl('/cart'))}
          onCheckout={() => navigate(getLocalizedUrl('/checkout'))}
        />
      </div>
    </HelmetProvider>
  );
}
